import React, { useState, useRef, useEffect } from 'react'
import { NavBlock } from 'src/components/blocks/Nav'
import FooterBlock from 'src/components/blocks/Footer'
import Layout from 'src/components/partials/Layout'
import SEO from 'src/components/partials/Seo'
import ComponentTitleLockup from 'src/modules/molecules/component-title-lockup'
import * as styles from './About.module.scss'
import ClientList from 'src/modules/molecules/client-list'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/effect-fade'
import { Navigation, EffectFade } from 'swiper/modules'
import { ReactSVG } from 'react-svg'
import InvestorList from 'src/modules/molecules/investor-list'
import TextWrap from 'src/modules/organisms/text-wrap'
import TestimonialsLoop from 'src/modules/organisms/testimonials-loop'

const AboutPage = () => {
  const slides = [
    {
      image: '/assets/images/staff/elisa.jpg',
      title: 'Elisa Dinsmore',
      subtitle: 'Customer Success Manager',
      body: '“I like being in environments where I’m encouraged to grow, and that’s exactly the what Flatfile has to offer. The problems we’re tackling require us to keep up with the fast-changing tech landscape, and I’m thrilled to work alongside people who are passionate about solving these problems and who encourage me to never stop getting better.”'
    },
    {
      image: '/assets/images/staff/roby.jpg',
      title: 'Roby Hyde',
      subtitle: 'Expert Customer Engineer',
      body: '“At Flatfile, I get to work closely with customers and help them solve all sorts of technology problems. I also get to use my skills and experience to help Flatfile continue on our massive growth trajectory. Those are key drivers for me and I’m excited that I get to do both here.”'
    },
    {
      image: '/assets/images/staff/damon.jpg',
      title: 'Damon Banks',
      subtitle: 'Staff Software Engineer',
      body: '“At Flatfile, I’ve experienced unmatched autonomy, trust, and ownership that have redefined my role as a Product Engineer. The culture encourages exploring emerging fields like Agentic AI and tackling complex customer challenges, fostering both growth and meaningful innovation.”'
    }
  ]

  const [activeImage, setActiveImage] = useState(slides[0].image)
  const [fadeClass, setFadeClass] = useState('')

  const handleImageChange = (newImage: string) => {
    setFadeClass(styles.fadeOut)
    setTimeout(() => {
      setActiveImage(newImage)
      setFadeClass(styles.fadeIn)
    }, 300)
  }

  const prevRef = useRef<HTMLButtonElement>(null)
  const nextRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (prevRef.current && nextRef.current) {
      const swiperElement = document.querySelector('.swiper') as HTMLElement & {
        swiper: any
      }
      const swiper = swiperElement?.swiper
      if (swiper) {
        swiper.params.navigation.prevEl = prevRef.current
        swiper.params.navigation.nextEl = nextRef.current
        swiper.navigation.init()
        swiper.navigation.update()
      }
    }
  }, [])

  return (
    <Layout>
      <NavBlock />
      <main className={styles.about}>
        <section className={styles.hero}>
          <ComponentTitleLockup
            headingLevel='h1'
            visualStyle='h2'
            title='We believe data'
            titleAlt='should migrate itself'
            bodyText1='Data that’s mapped, cleaned, transformed, and converted—powered by AI, guided by you.'
            align='center'
            layout='stacked'
          />
          <div className={styles.heroStats}>
            <div className={styles.heroStatsItem}>
              <h3>4.7x</h3>
              <p>faster data onboarding vs. manual processes</p>
            </div>
            <div className={styles.heroStatsDivider}></div>
            <div className={styles.heroStatsItem}>
              <h3>78%</h3>
              <p>of customers go live within 30 days</p>
            </div>
            <div className={styles.heroStatsDivider}></div>
            <div className={styles.heroStatsItem}>
              <h3>95%</h3>
              <p>of mapped fields are auto-detected using AI</p>
            </div>
          </div>
        </section>
        <section className={styles.mission}>
          <div className={styles.missionContainer}>
            <TextWrap
              headingLevel='h3'
              visualStyle='h3'
              title='Before Flatfile, data onboarding lived in the margins—'
              titleAlt='everyone needed it, but no one really owned it.'
              textWrap='mobileOnly'
              paragraphs={[
                'As product managers, we (David Boskovic & Eric Crane) saw the fallout firsthand. Every time a new customer sent over data, it kicked off the same chaos: brittle importers, manual field mapping, unclear formats, versioning issues, endless follow-up. What should’ve taken hours took weeks. What should’ve been seamless became a drag on every team involved—customer success, implementation, even engineering. Onboarding wasn’t just broken. It was no one’s job, but it remained everyone’s bottleneck.',
                'Naturally, we looked for a solution. From the start, we knew we didn’t want a spreadsheet hack or a patched-together integration—we wanted a product built for this exact pain. Because there wasn’t one. There were tools for syncing, storing, analyzing data. But nothing for getting it clean, structured, and production-ready. So we did what most teams do—we built a stopgap. Then another. And another. Still, as we worked, the pain of data migration didn’t just persist—it multiplied. That’s when it clicked: The problem wasn’t a lack of tooling. It was a lack of infrastructure. Every team was reinventing the wheel—solving the same edge cases, cleaning the same messy files, building workflows that would eventually break. What we needed wasn’t another feature. It was a foundation. One that could handle the nuance of real-world data, help teams move fast, without needing to be technical. One that could turn unstructured input into trusted outcomes—at scale.',
                'And that’s why Flatfile exists. It eliminates the unnecessary work between people and the data they depend on. Our platform automates the hardest parts of onboarding: mapping, transforming, and validating raw, unpredictable customer data. We started Flatfile because we believed there had to be a better way to onboard data. We’re still here because “better” isn’t a finish line—it’s a foundation.'
              ]}
            />
            <ClientList />
          </div>
        </section>
        <section className={styles.staff}>
          <div className={styles.staffContainer}>
            <ComponentTitleLockup
              headingLevel='h3'
              visualStyle='h3'
              title='Who we are is why it works'
              bodyText1='We didn’t hire a team to build Flatfile. We built Flatfile around a team who knew the problem firsthand. Engineers, solutions architects, and support pros, united by one goal: help teams onboard clean, usable data without needing to lean on engineers.'
              align='center'
              maxWidth='md'
            />
            <div className={styles.staffCarousel}>
              <div className={styles.staffCarouselImg}>
                <img
                  src={activeImage}
                  alt='Flatfile team'
                  className={fadeClass}
                />
              </div>
              <Swiper
                modules={[Navigation, EffectFade]}
                effect='fade'
                fadeEffect={{ crossFade: true }}
                slidesPerView={1}
                speed={300}
                onSlideChange={swiper =>
                  handleImageChange(slides[swiper.activeIndex].image)
                }
                className='staff-swiper'
              >
                {slides.map((slide, index) => (
                  <SwiperSlide key={index} className='staff-swiper__slide'>
                    <ComponentTitleLockup
                      headingLevel='h4'
                      visualStyle='h4'
                      title={slide.title}
                      titleAlt={slide.subtitle}
                      bodyText1={slide.body}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className='staff-swiper__navigation'>
                <button ref={prevRef} className='prev'>
                  <ReactSVG src='/assets/icons/left-arrow.svg' />
                </button>
                <button ref={nextRef} className='next'>
                  <ReactSVG src='/assets/icons/right-arrow.svg' />
                </button>
              </div>
            </div>
          </div>
        </section>
        <section className={styles.security}>
          <div className={styles.securityContainer}>
            <ComponentTitleLockup
              headingLevel='h3'
              visualStyle='h3'
              title='Our commitment to security'
              bodyText1='Flatfile is built on secure, modern cloud technologies. Partnering with Amazon S3, we provide industry-leading scalability and performance enterprise teams expect. Flatfile is SOC 2, HIPAA, and GDPR compliant, meeting the regulatory standards required to keep sensitive data secure. Every data transfer is encrypted using AES-256—one of the strongest block ciphers available—ensuring that information remains protected both in transit and at once it’s stored.'
              align='center'
              maxWidth='md'
            />
            <div className={styles.securityLogos}>
              <img src='/assets/logos/security/GDPR.png' alt='Security Logos' />
              <div className={styles.securityLogosDivider}></div>
              <img src='/assets/logos/security/SOC.png' alt='Security Logos' />
              <div className={styles.securityLogosDivider}></div>
              <img
                src='/assets/logos/security/SOC-2.png'
                alt='Security Logos'
              />
              <div className={styles.securityLogosDivider}></div>
              <img
                src='/assets/logos/security/privacy-shield.png'
                alt='Security Logos'
              />
              <div className={styles.securityLogosDivider}></div>
              <img
                src='/assets/logos/security/HIPAA.png'
                alt='Security Logos'
              />
            </div>
          </div>
        </section>
        <TestimonialsLoop
          title='Our customers are'
          titleAlt='our biggest fans'
          description='From global SaaS leaders to fast-moving implementation teams, our customers use Flatfile to accelerate data onboarding collaboratively. Their reviews tell the real story—across time saved, non-technical teams enabled, and data migration workflows that feel second nature.'
          buttonText='See G2 Reviews'
        />
        <section className={styles.raise}>
          <div className={styles.raiseContainer}>
            <TextWrap
              headingLevel='h3'
              visualStyle='h3'
              title="We've raised nearly $100M"
              titleAlt='Flatfile is backed by leading investors.'
              paragraphs={[
                'Flatfile has raised nearly $100 million from leading investors who share our belief that data onboarding is one of the most overlooked points in enterprise growth. Our latest Series B funding round was led by Tiger Global and joined by Gradient Ventures, Scale Venture Partners, Workday Ventures, Afore Capital, and Two Sigma Ventures.',
                'We’re also backed by angel investors from companies like Airtable, DocuSign, LinkedIn, and Gainsight—people who’ve experienced firsthand the pain of messy customer data at scale. “Flatfile is the trusted name for business-to-business data exchange,” said John Curtius, partner at Tiger Global. “Modern data exchange is a mission-critical challenge. Flatfile enables businesses to exchange customer data simply, securely, and efficiently with AI-assisted import flows, enabling all parties to accelerate return on investment.”',
                'This investment is accelerating the launch of our new developer platform, expanding Flatfile from an AI-assisted importer into a composable data exchange layer. Teams can now design secure, scalable workflows tailored to the way their customers actually work. Our goal is simple: make customer data usable, faster.'
              ]}
            />
            <InvestorList />
          </div>
        </section>
      </main>
      <FooterBlock />
    </Layout>
  )
}

export default AboutPage

export const Head = () => (
  <SEO
    description='Learn more about our mission and values.'
    title='About Us'
  />
)
